import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, ChevronDown, ChevronUp, Info, Lock, MapPinned, RotateCw, Search, Trash2 } from "lucide-react";
import GetRequest from "../../../Api/GetRequest";
import { D2C_OPS_SWITCHES_API_URL } from "../../Shared/constant";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input, NativeSelect } from "./ui/input";
import { Callout, Label } from "./ui/misc";
import { Switch } from "./ui/switch";
import { SegmentedControl, SegmentedItem } from "./ui/toggle-group";
import { Hint } from "./ui/tooltip";
import {
  AlertDialog,
  AlertDialogCancelButton,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { cn } from "./ui/utils";
import StateBlock, { Busy, EmptyState } from "./StateBlock";
import d2cPut from "./d2cPut";
import { errorMessage } from "./useD2cGet";
import {
  CHOICE_DEFAULT,
  CHOICE_OFF,
  CHOICE_ON,
  areaAllowed,
  buildSwitchPayload,
  changedLabels,
  cityChoice,
  confirmationLines,
  describeArea,
  regionChoice,
  regionEffect,
  removeUnknown,
  sameDraft,
  setCityChoice,
  setRegionChoice,
  toDraft,
  unknownIds,
} from "./d2cSwitches";

export const SWITCHES_READ_ONLY_TEXT =
  "You can view switches but not change them (needs the d2c-switches permission).";
export const SWITCHES_STALE_TEXT = "Someone changed the switches since you opened this page. Reload to see the latest.";

const MAX_CITY_ROWS = 60;

const CHOICES = [
  { value: CHOICE_DEFAULT, label: "Default" },
  { value: CHOICE_ON, label: "On" },
  { value: CHOICE_OFF, label: "Off" },
];

const EFFECTS = {
  on: { tone: "success", label: "Dealers get orders" },
  some: { tone: "warning", label: "Some cities" },
  off: { tone: "danger", label: "No dealer orders" },
};

// How many regions, cities and flags differ between two drafts.
const unsavedCount = (baseline, draft) => {
  let count = Number(baseline.routing_enabled !== draft.routing_enabled) + Number(baseline.auto_enrol !== draft.auto_enrol);
  const regionIds = new Set([...baseline.onRegions, ...baseline.offRegions, ...draft.onRegions, ...draft.offRegions]);
  regionIds.forEach((id) => {
    if (regionChoice(baseline, id) !== regionChoice(draft, id)) count += 1;
  });
  const cityIds = new Set([...baseline.onCities, ...baseline.offCities, ...draft.onCities, ...draft.offCities]);
  cityIds.forEach((id) => {
    if (cityChoice(baseline, id) !== cityChoice(draft, id)) count += 1;
  });
  return count;
};

const ChoiceControl = ({ value, onChange, disabled, label, size }) => (
  <SegmentedControl aria-label={label} value={value} onValueChange={onChange} disabled={disabled} size={size}>
    {CHOICES.map((choice) => (
      <SegmentedItem key={choice.value} value={choice.value} size={size} disabled={disabled}>
        {choice.label}
      </SegmentedItem>
    ))}
  </SegmentedControl>
);

const CityList = ({ region, draft, onCity, readOnly, lockText }) => {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const matches = (region.cities || []).filter((city) => !term || String(city.name).toLowerCase().includes(term));
  const shown = matches.slice(0, MAX_CITY_ROWS);

  return (
    <div className="tw-mt-3 tw-space-y-2 tw-border-t tw-pt-3">
      <div className="tw-relative">
        <Search className="tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 tw-size-4 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${region.cities.length} cities in ${region.name}`}
          aria-label={`Search cities in ${region.name}`}
          className="tw-h-8 tw-pl-8"
        />
      </div>
      {shown.length ? (
        <ul className="tw-m-0 tw-max-h-80 tw-list-none tw-divide-y tw-overflow-y-auto tw-p-0">
          {shown.map((city) => {
            const allowed = areaAllowed(draft, region, city.city_id);
            return (
              <li key={city.city_id} className="tw-flex tw-items-center tw-gap-2 tw-py-1.5">
                <span
                  aria-hidden="true"
                  className={cn("tw-size-2 tw-shrink-0 tw-rounded-full", allowed ? "tw-bg-emerald-500" : "tw-bg-red-400")}
                />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm">{city.name || "Unnamed city"}</span>
                <span className="tw-sr-only">{allowed ? "On" : "Off"}</span>
                <Hint text={lockText}>
                  <ChoiceControl
                    size="sm"
                    value={cityChoice(draft, city.city_id)}
                    onChange={(choice) => onCity(city.city_id, choice)}
                    disabled={readOnly}
                    label={`Routing in ${city.name}`}
                  />
                </Hint>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="tw-text-sm tw-text-muted-foreground">No city matches.</div>
      )}
      {matches.length > shown.length && (
        <div className="tw-text-xs tw-text-muted-foreground">
          Showing {shown.length} of {matches.length}. Search to find the rest.
        </div>
      )}
    </div>
  );
};

const RegionCard = ({ region, draft, onRegion, onCity, readOnly, lockText, expanded, onToggle }) => {
  const effect = regionEffect(draft, region);
  const shown = EFFECTS[effect.state];
  const cities = region.cities || [];
  const overridden = cities.filter((city) => cityChoice(draft, city.city_id) !== CHOICE_DEFAULT);

  return (
    <Card className="tw-p-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <div className="tw-truncate tw-text-sm tw-font-semibold">{region.name || "Unnamed region"}</div>
          <Badge tone={shown.tone} className="tw-mt-1">
            {shown.label}
            {effect.state === "some" && ` (${effect.onCities.length} of ${cities.length})`}
          </Badge>
        </div>
        <Hint text={lockText}>
          <ChoiceControl
            value={regionChoice(draft, region.region_id)}
            onChange={(choice) => onRegion(region.region_id, choice)}
            disabled={readOnly}
            label={`Routing in ${region.name}`}
          />
        </Hint>
      </div>

      {overridden.length > 0 && (
        <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-1.5" aria-label={`Cities set in ${region.name}`}>
          {overridden.map((city) => {
            const choice = cityChoice(draft, city.city_id);
            return (
              <Badge key={city.city_id} tone={choice === CHOICE_ON ? "success" : "danger"}>
                {city.name}: {choice === CHOICE_ON ? "On" : "Off"}
              </Badge>
            );
          })}
        </div>
      )}

      {cities.length > 0 && (
        <Button variant="ghost" size="sm" className="tw-mt-2 tw-px-2 tw-text-muted-foreground" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          Cities ({cities.length}
          {overridden.length ? `, ${overridden.length} set` : ""})
        </Button>
      )}
      {expanded && <CityList region={region} draft={draft} onCity={onCity} readOnly={readOnly} lockText={lockText} />}
    </Card>
  );
};

const SettingRow = ({ id, label, help, checked, onChange, disabled, lockText }) => (
  <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-py-3 first:tw-pt-0 last:tw-pb-0">
    <div className="tw-min-w-0">
      <Label htmlFor={id}>{label}</Label>
      <div className="tw-mt-0.5 tw-text-xs tw-leading-4 tw-text-muted-foreground">{help}</div>
    </div>
    <Hint text={lockText}>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </Hint>
  </div>
);

// The country, region and city switches for dealer routing, and auto-enrol.
// `switches` is the useD2cGet result for GET ops/switches, shared with the
// dashboard's region filter.
const RoutingSwitches = ({ switches, canEdit }) => {
  const { accessToken } = useSelector((state) => state.adminReducers);
  const countries = switches.data?.countries || [];
  const regions = switches.data?.regions || [];

  const [country, setCountry] = useState("");
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [checking, setChecking] = useState(false);
  const [stale, setStale] = useState(false);
  // A 409 SWITCHES_CHANGED answer: {message, current} (current = the server's row).
  const [conflict, setConflict] = useState(null);

  // Fresh server data replaces what the last PUT answered.
  useEffect(() => setSaved({}), [switches.data]);

  const row = countries.find((entry) => entry.country === country) || countries[0];
  const code = row?.country || "";
  const baseRow = (code && saved[code]) || row;
  const baseline = useMemo(() => toDraft(baseRow), [baseRow]);
  const current = draft || baseline;
  const payload = code ? buildSwitchPayload(code, baseline, current) : null;
  const readOnly = !canEdit || forbidden;
  const lockText = readOnly ? SWITCHES_READ_ONLY_TEXT : null;
  const unknown = unknownIds(current, regions);
  const pending = payload ? unsavedCount(baseline, current) : 0;

  const edit = (change) => {
    setNotice("");
    setError(null);
    setDraft(change(current));
  };

  const pickCountry = (value) => {
    setCountry(value);
    setDraft(null);
    setExpanded({});
    setError(null);
    setNotice("");
    setStale(false);
    setConflict(null);
  };

  // Before confirming, read the switches again: if someone else saved since
  // this page loaded, saving the draft would silently undo their change.
  const openConfirm = async () => {
    if (!payload) return;
    setChecking(true);
    setError(null);
    const response = await GetRequest(D2C_OPS_SWITCHES_API_URL, accessToken);
    setChecking(false);
    if (response?.status !== 200) {
      setError(errorMessage(response));
      return;
    }
    const freshRegions = response?.data?.regions || [];
    const freshRow = (response?.data?.countries || []).find((entry) => entry.country === code);
    const fresh = toDraft(freshRow);
    if (!sameDraft(fresh, baseline)) {
      setStale(true);
      return;
    }
    // The server refuses the PUT (409) if the row changed after this read.
    const body = freshRow?.version != null ? { ...payload, version: freshRow.version } : payload;
    setConfirm({ payload: body, lines: confirmationLines(code, fresh, current, freshRegions), everywhere: null });
  };

  const reloadLatest = () => {
    setStale(false);
    setDraft(null);
    setError(null);
    setNotice("");
    switches.reload();
  };

  // Load the row the 409 carried, dropping the draft. No retry: the user looks
  // at the latest switches and makes the change again if still wanted.
  const loadConflictRow = () => {
    const latest = conflict?.current;
    setConflict(null);
    setDraft(null);
    setError(null);
    setNotice("");
    if (latest?.country) setSaved((previous) => ({ ...previous, [latest.country]: latest }));
    else switches.reload();
  };

  const send = async (body) => {
    setSaving(true);
    setError(null);
    const response = await d2cPut(D2C_OPS_SWITCHES_API_URL, body, accessToken);
    setSaving(false);
    if (response?.status === 200) {
      setSaved((previous) => ({ ...previous, [code]: response.data }));
      setDraft(null);
      setConfirm(null);
      setNotice("Saved. Waiting orders follow the new switches within a minute.");
      switches.reload();
      return;
    }
    if (response?.status === 403) {
      setForbidden(true);
      setConfirm(null);
      setError(SWITCHES_READ_ONLY_TEXT);
      return;
    }
    if (response?.data?.code === "SWITCHES_CHANGED") {
      setConfirm(null);
      setConflict({ message: errorMessage(response), current: response?.data?.current });
      return;
    }
    if (response?.data?.code === "CONFIRM_EVERYWHERE") {
      setConfirm((previous) => previous && { ...previous, everywhere: errorMessage(response) });
      return;
    }
    setConfirm(null);
    setError(errorMessage(response));
  };

  const [nowLine, afterLine, ...restLines] = confirm?.lines || [];
  const hasBeforeAfter = Boolean(nowLine && nowLine.startsWith("Now:"));

  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
        <div>
          <h2 className="tw-m-0 tw-font-sans tw-text-base tw-font-semibold tw-leading-6">Routing switches</h2>
          <p className="tw-m-0 tw-mt-0.5 tw-max-w-2xl tw-text-sm tw-text-muted-foreground">
            Where website bike orders are offered to dealers. The server also has a master switch above these: while
            it is off, no order goes to dealers whatever this page shows.
          </p>
        </div>
        {countries.length > 1 && (
          <NativeSelect value={code} onChange={(e) => pickCountry(e.target.value)} aria-label="Country" className="tw-w-28">
            {countries.map((entry) => (
              <option key={entry.country} value={entry.country}>
                {entry.country}
              </option>
            ))}
          </NativeSelect>
        )}
      </div>

      <StateBlock
        loading={switches.loading}
        error={switches.error}
        onRetry={switches.reload}
        isEmpty={!row}
        emptyIcon={MapPinned}
        emptyText="No country has a switch row on the server yet, so the defaults apply: dealer routing is on everywhere, under the server's master switch. There is nothing to change here until the OMS creates a row."
      >
        {row && (
          <>
            <Callout tone="info" icon={MapPinned} aria-live="polite">
              <span className="tw-font-medium">{describeArea(current, regions, code).text}</span>
              {payload && <span className="tw-opacity-75"> (not saved yet)</span>}
            </Callout>

            {readOnly && (
              <Callout tone="warning" icon={Lock} role="note">
                {SWITCHES_READ_ONLY_TEXT}
              </Callout>
            )}

            <div className="tw-grid tw-gap-4 lg:tw-grid-cols-5">
              <Card className="lg:tw-col-span-3">
                <CardHeader className="tw-pb-2">
                  <div>
                    <CardTitle>Country settings</CardTitle>
                    <CardDescription>{code}</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="tw-divide-y">
                  <SettingRow
                    id="d2c-routing-enabled"
                    label={`Dealer routing in ${code}: ${current.routing_enabled ? "ON" : "OFF"}`}
                    help={`Off stops dealer routing everywhere in ${code}, whatever the regions and cities below say.`}
                    checked={current.routing_enabled}
                    onChange={(checked) => edit((d) => ({ ...d, routing_enabled: checked }))}
                    disabled={readOnly}
                    lockText={lockText}
                  />
                  <SettingRow
                    id="d2c-auto-enrol"
                    label={`Auto-enrol stores: ${current.auto_enrol ? "ON" : "OFF"}`}
                    help="When on, every active store with a location and a dealer user in a switched-on area joins D2C by itself within 5 minutes. Stores an admin removed are never re-added."
                    checked={current.auto_enrol}
                    onChange={(checked) => edit((d) => ({ ...d, auto_enrol: checked }))}
                    disabled={readOnly}
                    lockText={lockText}
                  />
                </CardContent>
              </Card>

              <Card className="tw-bg-muted/40 lg:tw-col-span-2">
                <CardHeader className="tw-pb-2">
                  <CardTitle className="tw-flex tw-items-center tw-gap-1.5">
                    <Info className="tw-size-4 tw-text-muted-foreground" aria-hidden="true" /> How regions and cities work
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="tw-m-0 tw-list-disc tw-space-y-1 tw-pl-4 tw-text-xs tw-leading-4 tw-text-muted-foreground">
                    <li>Each region and city is On, Off or Default. Default means it follows the wider setting.</li>
                    <li>Empty means everywhere: with nothing set to On, dealers get orders everywhere that is not Off.</li>
                    <li>If any region is On, only the On regions get dealer orders.</li>
                    <li>If a city is On, its region gets dealer orders only in its On cities.</li>
                    <li>The most specific setting wins: a city's own setting beats its region's, and Off beats On.</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {unknown.length > 0 && (
              <Callout tone="warning" icon={AlertTriangle}>
                <div className="tw-font-medium">Stored ids no longer in the OMS list</div>
                <div className="tw-mb-2 tw-text-xs">
                  A region or city was deleted after it was switched. The server refuses a save that still sends it, so
                  remove it here.
                </div>
                <ul className="tw-m-0 tw-list-none tw-space-y-1 tw-p-0">
                  {unknown.map((entry) => (
                    <li key={`${entry.kind}-${entry.choice}-${entry.id}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                      <span>
                        Unknown or deleted {entry.kind} ({entry.id.slice(0, 8)}…), set{" "}
                        {entry.choice === CHOICE_ON ? "On" : "Off"}
                      </span>
                      {!readOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => edit((d) => removeUnknown(d, entry))}
                          aria-label={`Remove unknown ${entry.kind} ${entry.id}`}
                        >
                          <Trash2 aria-hidden="true" /> Remove
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}

            {regions.length ? (
              <div className="tw-grid tw-gap-3 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                {regions.map((region) => (
                  <RegionCard
                    key={region.region_id}
                    region={region}
                    draft={current}
                    readOnly={readOnly}
                    lockText={lockText}
                    expanded={Boolean(expanded[region.region_id])}
                    onToggle={() =>
                      setExpanded((previous) => ({ ...previous, [region.region_id]: !previous[region.region_id] }))
                    }
                    onRegion={(id, choice) => edit((d) => setRegionChoice(d, id, choice))}
                    onCity={(id, choice) => edit((d) => setCityChoice(d, id, choice))}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <EmptyState icon={MapPinned}>The OMS has no regions on file.</EmptyState>
              </Card>
            )}

            {conflict && (
              <Callout
                tone="danger"
                icon={AlertTriangle}
                role="alert"
                action={
                  <Button size="sm" variant="outline" onClick={loadConflictRow}>
                    <RotateCw aria-hidden="true" /> Reload
                  </Button>
                }
              >
                {conflict.message}
              </Callout>
            )}
            {stale && (
              <Callout
                tone="danger"
                icon={AlertTriangle}
                role="alert"
                action={
                  <Button size="sm" variant="outline" onClick={reloadLatest}>
                    <RotateCw aria-hidden="true" /> Reload (drops your unsaved changes)
                  </Button>
                }
              >
                {SWITCHES_STALE_TEXT}
              </Callout>
            )}
            {error && (
              <Callout tone="danger" icon={AlertTriangle} role="alert">
                {error}
              </Callout>
            )}
            {notice && (
              <Callout tone="success" role="status">
                {notice}
              </Callout>
            )}

            {!readOnly && payload && (
              <div className="tw-sticky tw-bottom-0 tw-z-10 tw-pb-1">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-bg-background/95 tw-px-4 tw-py-3 tw-shadow-lg tw-backdrop-blur">
                  <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium">
                    <span aria-hidden="true" className="tw-size-2 tw-rounded-full tw-bg-amber-500" />
                    {pending} unsaved change{pending === 1 ? "" : "s"}
                  </span>
                  <div className="tw-flex tw-gap-2">
                    <Button variant="outline" size="sm" onClick={() => setDraft(null)} disabled={saving || checking}>
                      Discard
                    </Button>
                    <Button size="sm" onClick={openConfirm} disabled={saving || checking || stale || Boolean(conflict)}>
                      {saving || checking ? <Busy label={checking ? "Checking for changes…" : "Saving…"} /> : "Review & save"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </StateBlock>

      <AlertDialog open={Boolean(confirm)} onOpenChange={(open) => !open && !saving && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change dealer routing for {code}?</AlertDialogTitle>
            <AlertDialogDescription>Changing: {changedLabels(confirm?.payload).join(", ")}.</AlertDialogDescription>
          </AlertDialogHeader>
          {confirm && (
            <div className="tw-space-y-2">
              {hasBeforeAfter ? (
                <>
                  <div className="tw-rounded-md tw-border tw-bg-muted/50 tw-px-3 tw-py-2">
                    <div className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground">Before</div>
                    <div>{nowLine}</div>
                  </div>
                  <div className="tw-rounded-md tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2 tw-text-blue-900">
                    <div className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-opacity-75">After</div>
                    <div>{afterLine}</div>
                  </div>
                  <ul className="tw-m-0 tw-list-disc tw-space-y-1 tw-pl-5">
                    {restLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <ul className="tw-m-0 tw-list-disc tw-space-y-1 tw-pl-5">
                  {confirm.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              {confirm.everywhere && (
                <Callout tone="danger" icon={AlertTriangle} role="alert">
                  {confirm.everywhere}
                </Callout>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancelButton onClick={() => setConfirm(null)} disabled={saving}>
              Cancel
            </AlertDialogCancelButton>
            {confirm?.everywhere ? (
              <Button
                variant="destructive"
                onClick={() => send({ ...confirm.payload, confirm_everywhere: true })}
                disabled={saving}
              >
                {saving ? <Busy label="Saving…" /> : "Yes, everywhere"}
              </Button>
            ) : (
              <Button onClick={() => confirm && send(confirm.payload)} disabled={saving}>
                {saving ? <Busy label="Saving…" /> : "Save"}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoutingSwitches;
